/**
 * Plan autosave rules and practice shells — pure, so they can be tested.
 *
 * A shell is a day-of-week starting point ("Tuesday"). Opening one never
 * edits it: it starts a dated copy ("Tue 9/29 · Tuesday") and autosave then
 * writes to the copy. The shell changes only through "Edit shell".
 */
import type { PlanData } from '../types';

/** What autosave compares. Only the saved fields, `shell` normalised so an
 *  old row (no key) and `shell: false` look the same. */
export function planSignature(p: Pick<PlanData, 'name' | 'offFocus' | 'defFocus' | 'startTime' | 'periods' | 'shell'>): string {
  return JSON.stringify([p.name, p.offFocus || '', p.defFocus || '', p.startTime || '', p.periods ?? [], p.shell === true]);
}

/** The data written for a plan. `shell` is only written when true, so a
 *  plan that was never a shell saves exactly as it did before shells. */
export function planDataFor(p: PlanData, name = p.name): PlanData {
  const d: PlanData = { name, offFocus: p.offFocus, defFocus: p.defFocus, startTime: p.startTime, periods: p.periods };
  if (p.shell === true) d.shell = true;
  return d;
}

export interface AutosaveInput {
  signature: string;
  lastSavedSignature: string;
  canEdit: boolean;
  /** following someone else's live practice — the plan is their snapshot */
  following: boolean;
  /** a blank "New Practice" nobody has touched */
  untouchedDraft: boolean;
}

export function shouldAutosave(i: AutosaveInput): boolean {
  if (!i.canEdit || i.following) return false;
  if (i.untouchedDraft) return false;
  return i.signature !== i.lastSavedSignature;
}

const DAY_WORDS: [RegExp, number][] = [
  [/\bsun(day)?\b/i, 0],
  [/\bmon(day)?\b/i, 1],
  [/\btue(s|sday)?\b/i, 2],
  [/\bwed(nesday)?\b/i, 3],
  [/\bthu(r|rs|rsday)?\b/i, 4],
  [/\bfri(day)?\b/i, 5],
  [/\bsat(urday)?\b/i, 6],
];
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The weekday a shell's name refers to, or null. First match wins. */
export function weekdayInName(name: string): number | null {
  let best: { at: number; day: number } | null = null;
  for (const [re, day] of DAY_WORDS) {
    const m = re.exec(name);
    if (m && (!best || m.index < best.at)) best = { at: m.index, day };
  }
  return best ? best.day : null;
}

/** The practice a shell is being opened for: the next date (today counts)
 *  on the weekday its name mentions, or today if it names none. */
export function shellPracticeDate(shellName: string, now: Date): Date {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const want = weekdayInName(shellName);
  if (want === null) return d;
  d.setDate(d.getDate() + ((want - d.getDay() + 7) % 7));
  return d;
}

export function uniquePlanName(want: string, existing: string[]): string {
  const names = new Set(existing.map((n) => n.toLowerCase()));
  if (!names.has(want.toLowerCase())) return want;
  for (let n = 2; n < 100; n++) {
    const t = `${want} (${n})`;
    if (!names.has(t.toLowerCase())) return t;
  }
  return `${want} ${Date.now()}`;
}

/** "Tue 9/29 · Tuesday", made unique against the team's live plan names. */
export function shellCopyName(shellName: string, now: Date, existing: string[]): string {
  const d = shellPracticeDate(shellName, now);
  return uniquePlanName(`${DAY_SHORT[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()} · ${shellName}`, existing);
}

/** The plan a shell starts: a copy with the shell flag off. */
export function planFromShell(shell: PlanData, name: string): PlanData {
  return planDataFor({ ...shell, shell: false }, name);
}
